import { E as EVENTS } from './events-58bc9098.js';

const { SHOPIFY_BLOCK_SELECT } = EVENTS;

class FeaturedLinks extends HTMLElement {
  constructor() {
    super();

    this.selectors = {
      link: "[js-featured-links-link]",
      linkByBlockId: (id) => `${this.selectors.link}[data-block-id="${id}"]`,
      image: "[js-featured-links-image]",
      parentSection: ".shopify-section",
    };

    this.classes = {
      current: "current",
      animateOut: "animate-out",
      animateIn: "animate-in",
      animateBack: "animate-back",
      active: "is-active",
    };
  }

  connectedCallback() {
    this.controller = new AbortController();
    this.linkImagePairs = {};
    this.currentIndex = "0";

    const eventOptions = { signal: this.controller.signal };
    const links = this.querySelectorAll(this.selectors.link);
    const images = this.querySelectorAll(this.selectors.image);

    // Build an object to hold link/image pairs for a given index
    images.forEach((image) => {
      const index = image.dataset.index;
      const link = Array.from(links).find((link) => {
        return link.dataset.index === index
      });

      this.linkImagePairs[index] = {
        image,
        link,
      };

      if (link) {
        link.addEventListener(
          "mouseover",
          this.handleMouseover.bind(this),
          eventOptions,
        );
      }
    });

    // Activate first link by default
    const firstLink = this.querySelector(this.selectors.link);
    if (firstLink) firstLink.classList.add(this.classes.active);

    // Block events
    if (window.Shopify.designMode) {
      this.parentSection = this.closest(this.selectors.parentSection);
      this.parentSection.addEventListener(
        SHOPIFY_BLOCK_SELECT,
        this.handleBlockSelect.bind(this),
        eventOptions,
      );
    }

    // Mobile: move button below image and start slideshow
    this.slideshowInterval = null;
    if (window.matchMedia('(max-width: 719.9px)').matches) {
      this.moveButtonBelowImage();
      this.startSlideshow();
    }
  }

  moveButtonBelowImage() {
    const sectionInner = this.querySelector('.section-inner');
    const linksWrapper = this.querySelector('.featured-links__links-wrapper');
    if (!sectionInner || !linksWrapper) return;

    const buttonWrapper = linksWrapper.querySelector('.btn-wrapper');
    if (!buttonWrapper) return;

    sectionInner.appendChild(buttonWrapper);
  }

  startSlideshow() {
    this.slideshowInterval = setInterval(() => {
      this.advanceSlideshow();
    }, 2000);
  }

  stopSlideshow() {
    if (this.slideshowInterval) {
      clearInterval(this.slideshowInterval);
      this.slideshowInterval = null;
    }
  }

  advanceSlideshow() {
    const indices = Object.keys(this.linkImagePairs).sort((a, b) => Number(a) - Number(b));
    if (indices.length < 2) return;

    const currentPos = indices.indexOf(this.currentIndex);
    const nextPos = (currentPos + 1) % indices.length;
    const nextIndex = indices[nextPos];

    this.querySelectorAll(this.selectors.link).forEach((link) => {
      link.classList.remove(this.classes.active);
    });
    const nextLink = this.linkImagePairs[nextIndex]?.link;
    if (nextLink) nextLink.classList.add(this.classes.active);

    const animatingOutIndex = this.currentIndex;
    this.currentIndex = nextIndex;
    this.updateImage(animatingOutIndex, nextIndex);
  }

  handleMouseover(event) {
    const hoveredLink = event.currentTarget;

    // Update active class
    this.querySelectorAll(this.selectors.link).forEach((link) => {
      link.classList.remove(this.classes.active);
    });
    hoveredLink.classList.add(this.classes.active);

    if (hoveredLink.dataset.index === this.currentIndex) return

    const animatingOutIndex = this.currentIndex;
    const animatingInIndex = hoveredLink.dataset.index;

    this.currentIndex = animatingInIndex;
    this.updateImage(animatingOutIndex, animatingInIndex);
  }

  updateImage(animatingOutIndex, animatingInIndex) {
    this.animate(animatingOutIndex, this.classes.animateOut);

    // The animation is different depending on whether the index of the
    // new link is higher or lower than the current index.
    if (animatingInIndex < animatingOutIndex) {
      this.animate(animatingInIndex, this.classes.animateBack);
    } else {
      this.animate(animatingInIndex, this.classes.animateIn);
    }
  }

  animate(index, animationClass) {
    const pair = this.linkImagePairs[index];
    if (!pair) return

    const image = pair.image;

    if (!image) return

    image.addEventListener(
      "animationend",
      () => {
        image.classList.remove(animationClass);

        if (animationClass === this.classes.animateOut) {
          image.classList.remove(this.classes.current);
        } else {
          image.classList.add(this.classes.current);
        }
      },
      {
        signal: this.controller.signal,
      },
    );

    image.classList.add(animationClass);
  }

  handleBlockSelect(event) {
    const selectedLink = this.querySelector(
      this.selectors.linkByBlockId(event.detail.blockId),
    );

    if (selectedLink.dataset.index === this.currentIndex) return

    const animatingOutIndex = this.currentIndex;
    const animatingInIndex = selectedLink.dataset.featuredLink;

    this.updateImage(animatingOutIndex, animatingInIndex);
  }

  disconnectedCallback() {
    this.stopSlideshow();
    this.controller.abort();
  }
}

if (!customElements.get("featured-links")) {
  customElements.define("featured-links", FeaturedLinks);
}
